/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { BlogPost, AppPage } from '../types';

import { getMultiCdnUrl } from '../utils/imageUtils';

interface PortfolioCarouselProps {
    posts: BlogPost[];
    onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const PortfolioCarousel: React.FC<PortfolioCarouselProps> = ({ posts, onNavigate }) => {
    const { t } = useTranslation();

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.4 }
        }
    };

    return (
        <motion.section
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full py-12"
        >
            <div className="max-w-7xl mx-auto px-4">
                <motion.div variants={cardVariants} className="text-center mb-8">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 dark:text-white mb-2">
                        Discover Our Style Stories
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        Explore the latest fashion insights and styling tips
                    </p>
                </motion.div>

                {/* Carousel Container */}
                <div className="relative">
                    <div className="overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth pb-4">
                        <div className="flex gap-6 px-2">
                            {posts.map((post, index) => (
                                <motion.div
                                    key={post.id}
                                    variants={cardVariants}
                                    whileHover={{ y: -8, transition: { duration: 0.3 } }}
                                    className="flex-shrink-0 w-[280px] md:w-[320px] snap-center"
                                >
                                    <div
                                        onClick={() => onNavigate('blog-post', { postId: post.id })}
                                        className="group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-shadow duration-300"
                                    >
                                        {/* Image */}
                                        <img
                                            src={getMultiCdnUrl(post.imageUrl, 'BLOG')}
                                            alt={t(post.titleKey)}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                            loading="lazy"
                                        />

                                        {/* Gradient Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />

                                        {/* Content Overlay */}
                                        <div className="absolute inset-0 p-6 flex flex-col justify-end">
                                            <h3 className="text-white font-serif text-xl md:text-2xl font-bold mb-2 line-clamp-2">
                                                {t(post.titleKey)}
                                            </h3>
                                            <p className="text-gray-200 text-sm mb-4 line-clamp-3 font-sans">
                                                {t(post.excerptKey)}
                                            </p>

                                            {/* Read More Button */}
                                            <div className="flex items-center text-white font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                                                <span>Read More</span>
                                                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                                            </div>
                                        </div>

                                        {/* Hover Border Effect */}
                                        <div className="absolute inset-0 border-2 border-white/0 group-hover:border-white/20 rounded-2xl transition-colors duration-300 pointer-events-none" />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Scroll Hint */}
                    <div className="flex justify-center mt-4 gap-2">
                        {posts.map((_, index) => (
                            <div
                                key={index}
                                className="h-1.5 w-6 rounded-full bg-gray-300 dark:bg-gray-600"
                            />
                        ))}
                    </div>
                </div>

                {/* View All Link */}
                <motion.div variants={cardVariants} className="text-center mt-8">
                    <button
                        onClick={() => onNavigate('blog')}
                        className="inline-flex items-center gap-2 text-gray-900 dark:text-white font-semibold hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-300"
                    >
                        <span>View All Stories</span>
                        <span className="text-xl">→</span>
                    </button>
                </motion.div>
            </div>
        </motion.section>
    );
};

export default PortfolioCarousel;
